/**
 * A workspace's name when the person already holds one called that (step
 * 9.11 of docs/plans/onboarding-harness/phase-09.md): how two names are
 * compared (the gap engine's `name_taken`, the build's refusal), and the
 * names offered instead, by the canvas head's popover and by OS's question
 * in the thread alike, so answering either closes both.
 *
 * Pure: the phone bundles it.
 */
import { COMPOSE_LIMITS } from './compose';

/** Two workspace names are the same when they match trimmed and without case. */
export const sameWorkspaceName = (a: string, b: string): boolean => {
  const one = a.trim();
  return one !== '' && one.toLocaleLowerCase() === b.trim().toLocaleLowerCase();
};

/** The area a place is told apart by: its first part ("Kothrud, Pune" is Kothrud); null for none. */
export const areaOfPlace = (place: string | null | undefined): string | null => {
  const area = (place ?? '').split(',')[0]?.trim() ?? '';
  return area === '' ? null : area;
};

const firstTwoWords = (name: string): string => name.trim().split(/\s+/).slice(0, 2).join(' ');

/**
 * Other names for a workspace whose name is held: with a place, the name
 * with its area, then its first two words as that area's branch ("Ravi
 * Medical Stores, Baner", "Ravi Medical, Baner branch"); without one, none.
 * Never a held name, never past a business name's ceiling, at most two.
 */
export const nameSuggestionsOf = (name: string, { place, held }: { readonly place: string | null; readonly held: readonly string[] }): string[] => {
  const area = place?.trim() ?? '';
  if (area === '' || name.trim() === '') return [];
  const out: string[] = [];
  for (const one of [`${name.trim()}, ${area}`, `${firstTwoWords(name)}, ${area} branch`]) {
    if (one.length > COMPOSE_LIMITS.businessName) continue;
    if (held.some((taken) => sameWorkspaceName(one, taken)) || out.some((kept) => sameWorkspaceName(one, kept))) continue;
    out.push(one);
  }
  return out.slice(0, 2);
};
